import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
} from 'react';

import { Search, HelpOutline } from '@mui/icons-material';
import {
  FilledInput,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Box,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';

import {
  currentPageIndexGeneralSearchAtom,
  currentPageIndexSpecificSearchAtom,
  navbarHeightAtom,
  pageSizeAtom,
  searchKeywordAtom,
  startRowGeneralSearchAtom,
  startRowSpecificSearchAtom,
} from '../utils/AtomConfig';

// Moved outside component as it's a pure function
function containsDangerousKql(input: string): boolean {
  const dangerousPatterns = [
    /\b(Path|SiteTitle|SPSiteUrl|WebId|ListId|ContentSource|ContentClass|ContentType|IsDocument)\s*:/i, // scope & type
  ];

  return dangerousPatterns.some((pattern) => pattern.test(input));
}

export const SearchBox: FunctionComponent = () => {
  const setSearchKeyword = useSetAtom(searchKeywordAtom);
  const setCurrentPageIndexSpecificSearch = useSetAtom(
    currentPageIndexSpecificSearchAtom
  );
  const setStartRowSpecificSearch = useSetAtom(startRowSpecificSearchAtom);
  const setCurrentPageIndexGeneralSearch = useSetAtom(
    currentPageIndexGeneralSearchAtom
  );
  const setStartRowGeneralSearch = useSetAtom(startRowGeneralSearchAtom);
  const navbarHeight = useAtomValue(navbarHeightAtom);
  const [pageSize, setPageSize] = useAtom(pageSizeAtom);

  const [keyword, setKeyword] = useState('');
  const [message, setMessage] = useState('');
  const [openHelpDialog, setOpenHelpDialog] = useState(false);

  const search = useCallback(
    (input: string) => {
      if (containsDangerousKql(input)) {
        setMessage(
          'Your search appears to contain restricted keywords. Please try again using more general terms.'
        );
        return;
      }
      setCurrentPageIndexSpecificSearch(1);
      setCurrentPageIndexGeneralSearch(1);
      setStartRowSpecificSearch(0);
      setStartRowGeneralSearch(0);
      setSearchKeyword(input);
    },
    [
      setSearchKeyword,
      setMessage,
      setCurrentPageIndexSpecificSearch,
      setStartRowSpecificSearch,
      setCurrentPageIndexGeneralSearch,
      setStartRowGeneralSearch,
    ]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      search(keyword);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [keyword, search]);

  const handleOpenHelpDialog = () => {
    setOpenHelpDialog(true);
  };

  const handleCloseHelpDialog = () => {
    setOpenHelpDialog(false);
  };

  function resetSearchResultIndexes() {
    setCurrentPageIndexSpecificSearch(1);
    setCurrentPageIndexGeneralSearch(1);
    setStartRowSpecificSearch(0);
    setStartRowGeneralSearch(0);
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingTop: `${navbarHeight}px`,
        }}
      >
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <FormControl
            sx={{ width: '100%', maxWidth: '640px', paddingRight: 1 }}
            variant='filled'
          >
            <InputLabel htmlFor='search-by-keyword'>Search</InputLabel>
            <FilledInput
              id='search-by-keyword'
              type='text'
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  search(keyword);
                }
              }}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='search by keyword'
                    onClick={() => search(keyword)}
                    edge='end'
                  >
                    <Search />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          <Button
            onClick={handleOpenHelpDialog}
            sx={{
              marginRight: 1,
              display: 'flex',
              gap: '4px',
              textTransform: 'initial',
            }}
          >
            <HelpOutline />
            <Typography sx={{ textDecoration: 'underline' }}>
              Search Tips
            </Typography>
          </Button>
        </Box>
        <Box>
          <FormControl sx={{ width: '100px' }}>
            <InputLabel htmlFor='page-size'>Page Size</InputLabel>
            <Select
              id='page-size'
              value={pageSize}
              label='Page size'
              onChange={(event) => {
                resetSearchResultIndexes();
                setPageSize(event.target.value as number);
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage('')}
      >
        <Alert severity='warning' variant='filled' sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>

      <Dialog open={openHelpDialog} onClose={handleCloseHelpDialog}>
        <DialogTitle>Search Tips</DialogTitle>
        <DialogContent>
          <section>
            <Typography sx={{ fontWeight: 600, fontSize: '1.2rem'}}>Results - By Title/Document Number</Typography>
            <p>
              PLEASE NOTE: You do not have to use{' '}
              <strong>TITLE</strong> in the search anymore - this top section
              has been updated to apply your search term to search on either the
              document Title <strong>OR</strong> Document ID - that is, use{' '}
              <em>Lightning</em> or <em>00004013</em>.
            </p>

            <ul>
              <li>
                Key words (<strong>AND</strong>, <strong>OR</strong>,{' '}
                <strong>NOT</strong>) are <strong>NOT</strong> applied in this
                section.
              </li>
              <li>
                You can still use a wildcard '*' if you only know part of a
                phrase or word (e.g., prefix/suffix matching such as{' '}
                <em>*point</em> or <em>share*</em>).
              </li>
            </ul>
          </section>

          <section>
            <Typography sx={{ fontWeight: 600, fontSize: '1.2rem'}}>Results - By Context</Typography>
            <p>
              This is the standard search by context where system ranking,
              logic, and key words <strong>DO</strong> apply:
            </p>

            <ul>
              <li>
                <strong>NOT</strong> - include the first word only if the second
                word is not present (e.g. <em>coal NOT gas</em>).
              </li>
              <li>
                <strong>OR</strong> - to expand results (e.g.{' '}
                <em>enclosed OR space</em>).
              </li>
              <li>
                <strong>AND</strong> - to narrow results (e.g.{' '}
                <em>enclosed AND space</em>).
              </li>
              <li>Don't worry about capitalization except for AND/OR.</li>
              <li>
                Use a wildcard '*' if you only know part of a phrase or word
                (e.g. <em>*share*</em>).
              </li>
              <li>
                If at first you don't succeed, try your search again with more
                words or different syntax.
              </li>
            </ul>
          </section>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHelpDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
